import React from "react";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/lib/i18n";

const SITE_URL = "https://wedocoaching.com";
const DEFAULT_OG = `${SITE_URL}/og-image.png`;

/**
 * Centralized SEO component.
 * Sets per-page <title>, meta description, canonical, Open Graph & Twitter cards.
 *
 * Usage:
 *   <SEO title="..." description="..." path="/dirigeants-codir" />
 */
export default function SEO({
  title,
  description,
  path = "/",
  image = DEFAULT_OG,
  type = "website",
  noindex = false,
  jsonLd = null,
}) {
  const { language } = useLanguage();
  const url = `${SITE_URL}${path}`;
  const fullTitle = title
    ? `${title} — WeDo Coaching & Conseil`
    : "WeDo Coaching & Conseil — make it matter.";

  return (
    <Helmet>
      <html lang={language || "fr"} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="WeDo Coaching & Conseil" />
      <meta property="og:locale" content={language === "en" ? "en_US" : "fr_FR"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

/* -------------------------------------------------------------------------- */
/* Reusable JSON-LD builders                                                  */
/* -------------------------------------------------------------------------- */

export const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "WeDo Coaching & Conseil",
  alternateName: "Wedo",
  url: SITE_URL,
  logo: `${SITE_URL}/brand/wedo_logo_2024.png`,
  image: DEFAULT_OG,
  description:
    "Cabinet premium de conseil et coaching pour dirigeants, CODIR, managers et organisations. Leadership, management, communication, gestion de crise, management de transition, structuration et transformation.",
  knowsAbout: [
    "Leadership",
    "Management",
    "Executive Coaching",
    "Communication",
    "Crisis Management",
    "Transition Management",
    "Organizational Structuring",
    "Process Communication Model",
    "Collective Transformation",
  ],
  founder: {
    "@type": "Person",
    name: "William Marques",
    jobTitle: "Fondateur · Coach & Conseil",
    sameAs: "https://www.linkedin.com/in/william-marques-coach/",
  },
  sameAs: ["https://www.linkedin.com/in/william-marques-coach/"],
  contactPoint: {
    "@type": "ContactPoint",
    email: "william@wedocoaching.com",
    contactType: "Customer Service",
    availableLanguage: ["French", "English"],
    areaServed: ["FR", "EU", "Worldwide"],
  },
  address: {
    "@type": "PostalAddress",
    addressCountry: "FR",
  },
};

export const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: "WeDo Coaching & Conseil",
  publisher: { "@id": `${SITE_URL}/#organization` },
  inLanguage: ["fr-FR", "en-US"],
};

export function serviceJsonLd({ name, description, path, serviceType }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url: `${SITE_URL}${path}`,
    serviceType,
    provider: { "@id": `${SITE_URL}/#organization` },
    areaServed: ["France", "Europe", "International"],
    availableLanguage: ["French", "English"],
  };
}

export function articleJsonLd({ title, description, slug, datePublished, dateModified, image }) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description,
    image: image || DEFAULT_OG,
    datePublished,
    dateModified: dateModified || datePublished,
    author: {
      "@type": "Person",
      name: "William Marques",
      url: "https://www.linkedin.com/in/william-marques-coach/",
    },
    publisher: { "@id": `${SITE_URL}/#organization` },
    mainEntityOfPage: `${SITE_URL}/blog/${slug}`,
  };
}
